import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Spin, Tag, message as antdMessage } from "antd";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import colors from "../shared/colors";
import {
  approveAgentProposal,
  createAgentSession,
  getAgentSession,
  isAgentRunInProgress,
  listAgentSessions,
  postAgentMessage,
  rejectAgentProposal,
  uploadAgentAttachment,
  waitForAgentRun,
  type AgentMessage,
  type AgentProposal,
  type AgentSession,
} from "../api/Agent";

const FIELD_LABELS_RU: Record<string, string> = {
  share_in_combo: "доля Combo",
  share_in_wildberries: "доля Wildberries",
  share_in_ozon: "доля Ozon",
  share_in_ya: "доля Яндекс Маркет",
  share_in_sber: "доля СберМегаМаркет",
  share_in_ali: "доля AliExpress",
  share_formula: "формула распределения МП",
  articles: "артикулы",
  tag_name: "тег",
  enabled: "FBS",
  is_active: "включён",
  order_limit: "лимит",
  meta_id: "группа лимита",
  kind: "тип склада",
  rows: "строки",
  items: "склады",
};

const formatProposalPatch = (patch: Record<string, unknown>): string => {
  return Object.entries(patch)
    .map(([key, value]) => {
      const label = FIELD_LABELS_RU[key]
        ? `${FIELD_LABELS_RU[key]} (${key})`
        : key;
      if (key === "articles" && Array.isArray(value)) {
        return `${label}: ${value.length} шт. — ${(value as string[])
          .slice(0, 8)
          .join(", ")}${value.length > 8 ? "…" : ""}`;
      }
      if (key === "rows" && Array.isArray(value)) {
        return `${label}: ${value.length} шт.`;
      }
      if (key === "items" && Array.isArray(value)) {
        return `${label}: ${value.length} шт.`;
      }
      if (key === "enabled" && typeof value === "boolean") {
        return `${label}: ${value ? "включить" : "выключить"}`;
      }
      if (key === "steps" && Array.isArray(value)) {
        return `шаги: ${(value as string[]).join(", ")}`;
      }
      return `${label} = ${value === null || value === undefined ? "—" : String(value)}`;
    })
    .join("\n");
};

const SCENARIOS: { title: string; hint: string; prompt: string }[] = [
  {
    title: "Тег по списку",
    hint: "Навесить или снять тег с артикулов из чата или файла",
    prompt:
      "Навесь тег ИМЯ_ТЕГА на артикулы: АРТИКУЛ1, АРТИКУЛ2. Чтобы снять: сними тег ИМЯ_ТЕГА с АРТИКУЛ1.",
  },
  {
    title: "FBS вкл/выкл",
    hint: "Включить или выключить FBS по списку",
    prompt: "Включи FBS для артикулов: АРТИКУЛ1, АРТИКУЛ2.",
  },
  {
    title: "Комбо и формула",
    hint: "Состав комбо и share_formula на складе интеграции WB/Ozon",
    prompt:
      "Проверь состав комбо АРТИКУЛ и формулу распределения WB (goods_mp_warehouse_stock.share_formula). Проставь формулу x/2 на состав для WB.",
  },
  {
    title: "Склады",
    hint: "Включить/выключить физ или WB/Ozon отдельно",
    prompt: "Выключи склад WB «ИМЯ». Если несколько совпадений — покажи список.",
  },
  {
    title: "Лимиты",
    hint: "Лимит и список артикулов, раздельно WB/Ozon",
    prompt:
      "Для Ozon поставь лимит 2000 на группу «ИМЯ» и добавь артикулы: АРТИКУЛ1, АРТИКУЛ2.",
  },
  {
    title: "Синх FBS",
    hint: "Запустить полный remnants_pipeline",
    prompt: "Запусти синхронизацию FBS.",
  },
  {
    title: "Watermarks",
    hint: "MP Sync Watermarks по артикулам",
    prompt: "Покажи MP Sync Watermarks по артикулам: АРТИКУЛ1, АРТИКУЛ2.",
  },
  {
    title: "Движение остатков",
    hint: "Объяснение за период: время и количество",
    prompt:
      "Покажи движение остатков артикула АРТИКУЛ за последние 24 часа: время и количество.",
  },
];

const Shell = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  background: ${colors.white};
  border: 1px solid ${colors.second};
  border-radius: 12px;
  overflow: hidden;
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: #f7fbfb;
  border-right: 1px solid ${colors.second};
`;

const SidebarHeader = styled.div`
  flex-shrink: 0;
  padding: 14px 12px;
  border-bottom: 1px solid ${colors.second};
`;

const SessionList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
`;

const SessionItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 4px;
  cursor: pointer;
  background: ${(p) => (p.$active ? colors.second : "transparent")};
  color: ${colors.black};
  font-size: 13px;
  &:hover {
    background: ${(p) => (p.$active ? colors.second : "#eef6f6")};
  }
`;

const Main = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: ${colors.white};
`;

const MainHeader = styled.div`
  flex-shrink: 0;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.second};
  font-weight: 600;
  color: ${colors.black};
`;

const Thread = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px 20px 12px;
`;

const ThreadInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled.div`
  max-width: 820px;
  margin: 40px auto 0;
  text-align: center;
`;

const EmptyTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 24px;
  color: ${colors.black};
`;

const EmptyText = styled.p`
  margin: 0 0 24px;
  color: #5a6a6a;
  font-size: 14px;
`;

const ScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  text-align: left;
`;

const ScenarioCard = styled.button`
  border: 1px solid ${colors.second};
  border-radius: 12px;
  padding: 14px;
  background: #f7fbfb;
  cursor: pointer;
  text-align: left;
  &:hover {
    border-color: ${colors.main};
    background: ${colors.second};
  }
`;

const ScenarioTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: ${colors.black};
`;

const ScenarioHint = styled.div`
  font-size: 12px;
  color: #5a6a6a;
`;

const Row = styled.div<{ $role: string }>`
  display: flex;
  justify-content: ${(p) => (p.$role === "user" ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div<{ $role: string }>`
  max-width: min(720px, 92%);
  padding: 12px 14px;
  border-radius: ${(p) =>
    p.$role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px"};
  background: ${(p) => (p.$role === "user" ? colors.main : "#f3f4f4")};
  color: ${(p) => (p.$role === "user" ? colors.white : colors.black)};
  white-space: pre-wrap;
  line-height: 1.45;
  font-size: 14px;
`;

const RoleLabel = styled.div`
  font-size: 11px;
  opacity: 0.75;
  margin-bottom: 4px;
`;

const ComposerWrap = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${colors.second};
  padding: 12px 16px 16px;
  background: ${colors.white};
`;

const ComposerInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const ComposerBox = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  border: 1px solid ${colors.second};
  border-radius: 16px;
  padding: 10px 10px 10px 14px;
  background: #f7fbfb;
  &:focus-within {
    border-color: ${colors.main};
  }
`;

const HintBar = styled.div`
  font-size: 12px;
  color: #7a8a8a;
  margin-top: 8px;
`;

const Proposals = styled.div`
  flex-shrink: 0;
  max-height: 28vh;
  overflow-y: auto;
  max-width: 820px;
  width: 100%;
  margin: 0 auto 12px;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProposalCard = styled.div`
  border: 1px solid ${colors.second};
  border-radius: 10px;
  padding: 12px;
  background: #fffdf5;
`;

const parseError = async (e: unknown) => {
  if (e instanceof Error && e.message) {
    try {
      const parsed = JSON.parse(e.message);
      return parsed.message || parsed.error || e.message;
    } catch {
      return e.message;
    }
  }
  return "Ошибка агента";
};

const AgentChat = () => {
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [proposals, setProposals] = useState<AgentProposal[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [uploading, setUploading] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bootOnce = useRef(false);
  const pollGen = useRef(0);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (threadRef.current) {
        threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }
    });
  };

  const refreshSessions = useCallback(async () => {
    const list = await listAgentSessions();
    setSessions(Array.isArray(list) ? list : []);
    return Array.isArray(list) ? list : [];
  }, []);

  const loadSession = useCallback(async (id: number) => {
    const data = await getAgentSession(id);
    if (!data?.session) {
      throw new Error("Не удалось открыть сессию");
    }
    setMessages(data.messages || []);
    setProposals(data.proposals || []);
    scrollToBottom();
    return data;
  }, []);

  const waitUntilRunSettled = useCallback(async (runId: number, sessionId: number) => {
    const gen = ++pollGen.current;
    setLoading(true);
    try {
      const run = await waitForAgentRun(runId, {
        isCancelled: () => pollGen.current !== gen,
      });
      if (!run || pollGen.current !== gen) return;
      await loadSession(sessionId);
      await refreshSessions();
    } finally {
      if (pollGen.current === gen) setLoading(false);
    }
  }, [loadSession, refreshSessions]);

  const openSession = useCallback(async (id: number) => {
    pollGen.current += 1;
    setActiveId(id);
    setLoading(false);
    const data = await loadSession(id);
    const run = data.latestRun;
    if (run && isAgentRunInProgress(run.status)) {
      await waitUntilRunSettled(run.id, id);
    }
  }, [loadSession, waitUntilRunSettled]);

  const ensureSession = useCallback(async (): Promise<number> => {
    if (activeId) return activeId;
    const s = await createAgentSession({ title: "Новый чат" });
    await refreshSessions();
    await openSession(s.id);
    return s.id;
  }, [activeId, openSession, refreshSessions]);

  useEffect(() => {
    if (bootOnce.current) return;
    bootOnce.current = true;

    const boot = async () => {
      setBooting(true);
      try {
        const ctx: Record<string, unknown> = {};
        const article = searchParams.get("article");
        const questionId = searchParams.get("questionId");
        const reviewId = searchParams.get("reviewId");
        if (article) ctx.article = article;
        if (questionId) ctx.questionId = Number(questionId);
        if (reviewId) ctx.reviewId = reviewId;

        if (Object.keys(ctx).length) {
          const s = await createAgentSession({
            title: article ? `Контекст ${article}` : "Новый чат",
            context: ctx,
          });
          await refreshSessions();
          await openSession(s.id);
          const hintParts = [
            article ? `артикул ${article}` : null,
            questionId ? `вопрос #${questionId}` : null,
            reviewId ? `отзыв #${reviewId}` : null,
          ].filter(Boolean);
          setText(`Помоги с: ${hintParts.join(", ")}`);
          return;
        }

        const list = await refreshSessions();
        if (list[0]) {
          await openSession(list[0].id);
        }
      } catch (e) {
        antdMessage.error(await parseError(e));
      } finally {
        setBooting(false);
      }
    };
    boot();
  }, [searchParams, refreshSessions, openSession]);

  const onCreate = async () => {
    try {
      const s = await createAgentSession({ title: "Новый чат" });
      await refreshSessions();
      await openSession(s.id);
      setText("");
    } catch (e) {
      antdMessage.error(await parseError(e));
    }
  };

  const onSend = async (overrideText?: string) => {
    const content = (overrideText ?? text).trim();
    if (!content || loading) return;

    setLoading(true);
    setText("");

    let sessionId: number;
    try {
      sessionId = await ensureSession();
    } catch (e) {
      setText(content);
      setLoading(false);
      antdMessage.error(await parseError(e));
      return;
    }

    const optimisticId = -Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        session_id: sessionId,
        role: "user",
        content,
        name: null,
        created_at: new Date().toISOString(),
      },
    ]);
    scrollToBottom();

    try {
      const { run } = await postAgentMessage(sessionId, content);
      try {
        await waitUntilRunSettled(run.id, sessionId);
      } catch (e) {
        antdMessage.error(await parseError(e));
        await loadSession(sessionId);
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setText(content);
      antdMessage.error(await parseError(e));
      setLoading(false);
    }
  };

  const onScenario = (prompt: string) => {
    setText(prompt);
  };

  const onApprove = async (id: number) => {
    try {
      await approveAgentProposal(id);
      antdMessage.success("Изменение применено");
      if (activeId) await openSession(activeId);
    } catch (e) {
      antdMessage.error(await parseError(e));
    }
  };

  const onReject = async (id: number) => {
    try {
      await rejectAgentProposal(id);
      if (activeId) await openSession(activeId);
    } catch (e) {
      antdMessage.error(await parseError(e));
    }
  };

  const onUploadFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const sessionId = await ensureSession();
      const result = await uploadAgentAttachment(sessionId, file);
      const kind = result?.kind || result?.attachment?.kind;
      antdMessage.success(
        kind === "catalog_import"
          ? "Файл 1С распознан, dry-run готов"
          : "Файл прикреплён к чату",
      );
      await openSession(sessionId);
      await refreshSessions();
      if (kind === "catalog_import") {
        setText(
          "Покажи сводку импорта и предложи применить categories+skeleton (propose_catalog_apply).",
        );
      } else {
        setText(
          `Учти прикреплённый файл «${file.name}» в ответе. Что с ним сделать?`,
        );
      }
    } catch (e) {
      antdMessage.error(await parseError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pendingProposals = proposals.filter((p) => p.status === "pending");
  const showEmpty = !booting && messages.length === 0 && !loading;

  return (
    <Shell>
      <Sidebar>
        <SidebarHeader>
          <Button type="primary" block onClick={onCreate}>
            + Новый чат
          </Button>
        </SidebarHeader>
        <SessionList>
          {sessions.map((item) => (
            <SessionItem
              key={item.id}
              type="button"
              $active={item.id === activeId}
              onClick={() => openSession(item.id).catch(async (e) => {
                antdMessage.error(await parseError(e));
              })}
            >
              {item.title || `Чат #${item.id}`}
              <div style={{ fontSize: 11, opacity: 0.6 }}>#{item.id}</div>
            </SessionItem>
          ))}
          {!sessions.length && !booting && (
            <div style={{ padding: 12, fontSize: 12, color: "#7a8a8a" }}>
              Пока нет чатов — нажмите «Новый чат» или отправьте сообщение.
            </div>
          )}
        </SessionList>
      </Sidebar>

      <Main>
        <MainHeader>Ассистент менеджера</MainHeader>

        <Thread ref={threadRef}>
          {booting ? (
            <div style={{ textAlign: "center", paddingTop: 80 }}>
              <Spin />
            </div>
          ) : showEmpty ? (
            <EmptyState>
              <EmptyTitle>Чем помочь?</EmptyTitle>
              <EmptyText>
                Напишите задачу своими словами или выберите сценарий. Изменения
                в данные агент только предлагает — подтвердите Approve в карточке
                предложения.
              </EmptyText>
              <ScenarioGrid>
                {SCENARIOS.map((s) => (
                  <ScenarioCard
                    key={s.title}
                    type="button"
                    onClick={() => onScenario(s.prompt)}
                  >
                    <ScenarioTitle>{s.title}</ScenarioTitle>
                    <ScenarioHint>{s.hint}</ScenarioHint>
                  </ScenarioCard>
                ))}
              </ScenarioGrid>
              <EmptyText style={{ marginTop: 20, marginBottom: 0 }}>
                Примеры: «поставь формулу FBS x/2 на WB для 12345», «почему
                sync_error на Ozon у артикула …», «ответь на вопрос #42».
              </EmptyText>
            </EmptyState>
          ) : (
            <ThreadInner>
              {messages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .map((m) => (
                  <Row key={m.id} $role={m.role}>
                    <Bubble $role={m.role}>
                      <RoleLabel>
                        {m.role === "user" ? "Вы" : "Ассистент"}
                      </RoleLabel>
                      {m.content}
                    </Bubble>
                  </Row>
                ))}
              {loading && (
                <Row $role="assistant">
                  <Bubble $role="assistant">
                    <Spin size="small" /> Думаю, ответ может занять несколько минут…
                  </Bubble>
                </Row>
              )}
            </ThreadInner>
          )}
        </Thread>

        {pendingProposals.length > 0 && (
          <Proposals>
            {pendingProposals.map((p) => (
              <ProposalCard key={p.id}>
                <div style={{ marginBottom: 8 }}>
                  <Tag color="gold">ожидает подтверждения</Tag>
                  <Tag>{p.kind}</Tag>
                </div>
                <div style={{ marginBottom: 8 }}>{p.summary}</div>
                <div
                  style={{
                    fontSize: 13,
                    margin: "0 0 10px",
                    whiteSpace: "pre-wrap",
                    background: "#fff",
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #eee",
                  }}
                >
                  {formatProposalPatch(p.patch)}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button type="primary" size="small" onClick={() => onApprove(p.id)}>
                    Approve
                  </Button>
                  <Button size="small" onClick={() => onReject(p.id)}>
                    Reject
                  </Button>
                </div>
              </ProposalCard>
            ))}
          </Proposals>
        )}

        <ComposerWrap>
          <ComposerInner>
            <ComposerBox>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.json,.xlsx,.xls,text/csv,application/json"
                style={{ display: "none" }}
                onChange={(e) =>
                  onUploadFile(e.target.files?.[0] || null).catch(() => undefined)
                }
              />
              <Button
                loading={uploading}
                disabled={loading}
                onClick={() => fileInputRef.current?.click()}
              >
                Файл
              </Button>
              <Input.TextArea
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoSize={{ minRows: 1, maxRows: 6 }}
                bordered={false}
                disabled={loading || uploading}
                placeholder="Сообщение ассистенту… (Enter — отправить, Shift+Enter — новая строка)"
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                style={{ flex: 1, resize: "none", background: "transparent" }}
              />
              <Button
                type="primary"
                loading={loading}
                disabled={!text.trim() || uploading}
                onClick={() => onSend()}
              >
                Отправить
              </Button>
            </ComposerBox>
            <HintBar>
              Сценарии: бренд/категория · формулы FBS · sync_error · вопросы ·
              отзывы · импорт 1С. Кнопка «Файл» — вложение в чат (CSV 1С
              распознаётся автоматически). Изменения — только через Approve.
            </HintBar>
          </ComposerInner>
        </ComposerWrap>
      </Main>
    </Shell>
  );
};

export default AgentChat;
